import React, { useEffect, useMemo, useState } from 'react';
import { Button, Icons } from '@ohif/ui-next';
import { useSystem } from '@ohif/core';
import { UploadRow, UploadStatus } from '../../utils/uploadTypes';
import type { UploadStats } from '../../hooks/useDicomUpload';
import FileList from './FileList';

interface UploadProgressProps {
  rows: UploadRow[];
  activeIndex: number;
  stats: UploadStats;
  isUploading: boolean;
  onCancelAll: () => void;
}

export default function UploadProgress({
  rows,
  activeIndex,
  stats,
  isUploading,
  onCancelAll,
}: UploadProgressProps) {
  const { servicesManager } = useSystem();
  const [showFailedOnly, setShowFailedOnly] = useState(false);

  useEffect(() => {
    if (stats.failedFiles === 0) {
      setShowFailedOnly(false);
    }
  }, [stats.failedFiles]);

  const ProgressLoadingBar = (
    servicesManager.services.customizationService as AppTypes.CustomizationService
  ).getCustomization('ui.progressLoadingBar') as React.ComponentType<{ progress?: number }>;

  const indices = useMemo(
    () =>
      rows
        .map((unused: UploadRow, index: number) => index)
        .filter(index => !showFailedOnly || rows[index].status === UploadStatus.Failed),
    [rows, showFailedOnly]
  );

  return (
    <div className="flex h-full flex-col p-4">
      <div className="border-input bg-background rounded-lg border px-4 py-4">
        <div className="flex items-center gap-2">
          <Icons.Info className="text-primary-light h-5 w-5" />
          <span className="text-xl font-semibold text-white">Uploading DICOM Files</span>
          <span
            className="text-muted-foreground ml-4 flex items-center gap-2 text-sm"
            data-cy="sycai-upload-counts"
          >
            <Icons.Series className="h-4 w-4" />
            {stats.completedFiles} of {stats.totalFiles} files uploaded
            {stats.failedFiles > 0 && <span>({stats.failedFiles} failed)</span>}
          </span>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="mr-4 flex-1">
            <ProgressLoadingBar progress={Math.min(100, Math.round(stats.totalProgress))} />
          </div>
          <div className="text-primary-light min-w-[48px] text-right text-sm font-medium">
            {Math.round(stats.totalProgress)}%
          </div>
          <div className="ml-4 h-8 w-8 flex-shrink-0">
            {stats.failedFiles > 0 ? (
              <Icons.StatusError className="text-destructive h-8 w-8" />
            ) : isUploading ? (
              <Icons.LoadingSpinner className="text-primary-light h-8 w-8 animate-spin" />
            ) : null}
          </div>
        </div>
      </div>

      <div className="mb-2 mt-4 flex justify-end gap-2">
        {stats.failedFiles > 0 && (
          <Button
            variant="secondary"
            size="sm"
            dataCY="sycai-upload-failed-toggle"
            onClick={() => setShowFailedOnly(!showFailedOnly)}
          >
            {showFailedOnly ? 'Show All' : 'Show Failed Only'}
          </Button>
        )}
        {isUploading && (
          <Button
            variant="ghost"
            size="sm"
            className="hover:text-foreground/60 hover:bg-transparent"
            dataCY="sycai-upload-cancel-all"
            onClick={onCancelAll}
          >
            Cancel All Uploads
          </Button>
        )}
      </div>

      <FileList
        rows={rows}
        activeIndex={activeIndex}
        indices={indices}
      />
    </div>
  );
}
